import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
  Error as FormError,
} from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import InfoBox from "../../components/shared/InfoBox";
import AlertDialog from "../../components/shared/AlertDialog";
import { CTADropdown } from "../../components/shared/Dropdown";
import {
  useGetAssociatedUsers,
  useGetRoles,
  useSaveUsers,
} from "../../queries/usersRoles";
import { useUserDetails } from "../../queries";
import {
  AclList,
  Action,
  AssociatedUsers,
  Resource,
  Roles,
} from "../../utils/interfaces/Companies";
import { ManagerPermissionPicker } from "../../components/ManagerPermissionPicker";

const ROLE_DESCRIPTIONS: Record<
  string,
  { scope: string; description: string }
> = {
  ADMIN: {
    scope: "Full access",
    description:
      "Can do anything: manage users, roles, grants, plans, employees, and all company settings.",
  },
  ADMIN_VIEWER: {
    scope: "Read-only",
    description:
      "Can view everything across the platform but cannot make any changes.",
  },
  APPROVER: {
    scope: "Creator + approvals",
    description:
      "Can create content and approve or reject grants and plans submitted for approval.",
  },
  CREATOR: {
    scope: "Create & manage",
    description:
      "Can create and edit grants, plans, employees, and documents. Cannot approve or change company settings.",
  },
  EMPLOYEE: {
    scope: "Self-service",
    description:
      "Can view their own grant details, vesting schedule, and personal documents.",
  },
  CHECKER: {
    scope: "Approval & verification",
    description:
      "Can review and verify grants and plans submitted for approval, but cannot create or manage them.",
  },
};

type RowStatus = "pristine" | "new" | "modified" | "deleted";

interface UserRow extends AssociatedUsers {
  _status: RowStatus;
  _prevStatus?: RowStatus;
  _localId: string;
}

function toUserRow(user: AssociatedUsers): UserRow {
  return { ...user, _status: "pristine", _localId: user.id ?? user.email };
}

/* ── Edit Permissions dialog ───────────────────────────────────────── */

function EditPermissionsDialog({
  initialPerms,
  userName,
  onClose,
  onSave,
}: {
  initialPerms: AclList[];
  userName: string;
  onClose: () => void;
  onSave: (perms: AclList[]) => void;
}) {
  const [perms, setPerms] = React.useState<AclList[]>(initialPerms);

  return (
    <VStack className="w-full">
      <HStack className="items-center justify-between px-6 py-5 border-b border-borderColor">
        <VStack className="gap-0.5">
          <p className="text-lg font-semibold text-headerColor">
            Edit Permissions
          </p>
          {userName && <p className="text-xs text-gray-400">{userName}</p>}
        </VStack>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Icon icon="lucide:x" width={20} />
        </button>
      </HStack>

      <VStack className="px-6 py-6">
        <div className="border border-borderColor rounded-lg p-4 bg-gray-50">
          <ManagerPermissionPicker value={perms} onChange={setPerms} />
        </div>
      </VStack>

      <HStack className="items-center justify-end gap-3 px-6 pb-6">
        <ButtonPrimary1 type="button" onClick={onClose}>
          Cancel
        </ButtonPrimary1>
        <ButtonPrimary type="button" onClick={() => onSave(perms)}>
          Save Permissions
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
}

/* ── Main page ─────────────────────────────────────────────────────── */

function UserRoles() {
  const { data: currentUser } = useUserDetails();
  const { data: associatedUsers } = useGetAssociatedUsers();
  const { data: allRoles } = useGetRoles();
  const { mutate: saveUsers, isLoading: isSaving } = useSaveUsers();

  const [rows, setRows] = useState<UserRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addDialog, setAddDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [rolesExpanded, setRolesExpanded] = useState(false);
  const [permEditTarget, setPermEditTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!associatedUsers) return;
    setRows(associatedUsers.map(toUserRow));
    setSelected(new Set());
  }, [associatedUsers]);

  const hasUnsavedChanges = rows.some((r) => r._status !== "pristine");
  const pendingCount = rows.filter((r) => r._status !== "pristine").length;

  // Rows that can be checked - excludes current user and already-deleted rows
  const selectableIds = rows
    .filter((r) => r.userId !== currentUser?.id && r._status !== "deleted")
    .map((r) => r._localId);

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  function toggleSelectAll(checked: boolean) {
    setSelected(checked ? new Set(selectableIds) : new Set());
  }

  function toggleRow(localId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(localId)) {
        next.delete(localId);
      } else {
        next.add(localId);
      }
      return next;
    });
  }

  function handleRoleChange(localId: string, roleName: string) {
    const role = allRoles?.find((r) => r.name === roleName);
    setRows((prev) =>
      prev.map((r) =>
        r._localId !== localId
          ? r
          : {
              ...r,
              role: { name: roleName, id: role?.id },
              _status: r._status === "new" ? "new" : "modified",
            }
      )
    );
  }

  function handleDeleteSelected() {
    setRows((prev) =>
      prev
        .map((r): UserRow | null => {
          if (!selected.has(r._localId)) return r;
          // Newly added rows never existed on the server - remove entirely
          if (r._status === "new") return null;
          // Existing rows: keep visible as "deleted" so the user sees the pending change
          return { ...r, _prevStatus: r._status, _status: "deleted" };
        })
        .filter((r): r is UserRow => r !== null)
    );
    setSelected(new Set());
    setConfirmDelete(false);
  }

  function handleUndoDelete(localId: string) {
    setRows((prev) =>
      prev.map((r) =>
        r._localId !== localId
          ? r
          : {
              ...r,
              _status: r._prevStatus ?? "pristine",
              _prevStatus: undefined,
            }
      )
    );
  }

  function handleDeleteSingle(localId: string) {
    setSelected(new Set([localId]));
    setConfirmDelete(true);
  }

  function handleAddUser(values: {
    name: string;
    email: string;
    roleId: string;
    customPermissions?: AclList[];
  }) {
    const role = allRoles?.find((r) => r.id === values.roleId);
    setRows((prev) => [
      ...prev,
      {
        id: undefined,
        email: values.email,
        name: values.name,
        role: { id: values.roleId, name: role?.name },
        isCompanySpecificRole: false,
        userSince: new Date().toISOString(),
        newlyAdded: true,
        customPermissions: values.customPermissions,
        _status: "new",
        _localId: `new-${values.email}`,
      },
    ]);
    setAddDialog(false);
  }

  function handleSave() {
    const activeCount = rows.filter((r) => r._status !== "deleted").length;
    if (activeCount === 0) {
      toast("Cannot remove all users", { type: "error" });
      return;
    }

    const payload: AssociatedUsers[] = rows.map(
      ({ _status, _prevStatus, _localId, ...user }) => ({
        ...user,
        newlyAdded: _status === "new",
        edited: _status === "modified",
        toBeDeleted: _status === "deleted",
      })
    );

    saveUsers(payload, {
      onSuccess: () => {
        toast("Users saved successfully", { type: "success" });
      },
      onError: () => {
        toast("Failed to save users", { type: "error" });
      },
    });
  }

  function handleSavePermissions(perms: AclList[]) {
    if (!permEditTarget) return;
    setRows((prev) =>
      prev.map((r) =>
        r._localId !== permEditTarget
          ? r
          : {
              ...r,
              customPermissions: perms,
              _status: r._status === "new" ? "new" : "modified",
            }
      )
    );
    setPermEditTarget(null);
  }

  function discardChanges() {
    if (associatedUsers) {
      setRows(associatedUsers.map(toUserRow));
      setSelected(new Set());
    }
  }

  // Emails already in the table (non-deleted) - used to block duplicates in the add dialog
  const existingEmails = rows
    .filter((r) => r._status !== "deleted")
    .map((r) => r.email.trim().toLowerCase());

  return (
    <VStack className="gap-4 h-full">
      {/* Page header */}
      <HStack className="items-center justify-between px-6 py-4 bg-white rounded-lg border border-borderColor drop-shadow-box">
        <p className="text-lg font-semibold text-headerColor">Users & Roles</p>
        <HStack className="items-center gap-2">
          <CTADropdown
            className="items-center"
            actions={[
              {
                name: "Delete Selected",
                disabled: selected.size === 0,
              },
            ]}
            onAction={(action) => {
              if (action.name === "Delete Selected") setConfirmDelete(true);
            }}
            render={() => (
              <HStack className="items-center h-8 px-3 rounded text-slate-dark bg-slate-light gap-1">
                <span className="text-xs font-medium">Bulk Actions</span>
                <Icon icon="ri:arrow-drop-down-line" height={20} />
              </HStack>
            )}
          />
          <ButtonPrimary onClick={() => setAddDialog(true)}>
            Add User
          </ButtonPrimary>
        </HStack>
      </HStack>

      {/* Unsaved changes banner */}
      {hasUnsavedChanges && (
        <HStack className="justify-end">
          <InfoBox
            message={`You have ${pendingCount} unsaved ${
              pendingCount === 1 ? "change" : "changes"
            }. Click Save to apply or Discard to revert.`}
          />
        </HStack>
      )}

      {/* Users table card */}
      <Box className="bg-white rounded-lg border border-borderColor drop-shadow-box overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borderColor bg-gray-50">
              <th className="w-12 px-6 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  className="rounded cursor-pointer accent-primary"
                />
              </th>
              {["Name", "Email", "Role", "Member Since"].map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
              <th className="px-4 py-3 w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-borderColor">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-12 text-center text-sm text-gray-400"
                >
                  No users yet. Click &ldquo;Add User&rdquo; to invite someone.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <UserTableRow
                  key={row._localId}
                  row={row}
                  isCurrentUser={row.userId === currentUser?.id}
                  isSelected={selected.has(row._localId)}
                  allRoles={allRoles ?? []}
                  onToggle={() => toggleRow(row._localId)}
                  onRoleChange={(name) => handleRoleChange(row._localId, name)}
                  onDelete={() => handleDeleteSingle(row._localId)}
                  onUndo={() => handleUndoDelete(row._localId)}
                  onEditPermissions={() => setPermEditTarget(row._localId)}
                />
              ))
            )}
          </tbody>
        </table>
      </Box>

      {/* Roles info panel */}
      <Box className="bg-white rounded-lg border border-borderColor drop-shadow-box overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-6 py-4 text-left"
          onClick={() => setRolesExpanded((v) => !v)}
        >
          <HStack className="items-center gap-2">
            <Icon icon="lucide:shield" width={16} className="text-gray-500" />
            <p className="text-sm font-semibold text-headerColor">
              About Roles
            </p>
            <p className="text-xs text-gray-400">what each role can do</p>
          </HStack>
          <Icon
            icon={rolesExpanded ? "lucide:chevron-up" : "lucide:chevron-down"}
            width={16}
            className="text-gray-400"
          />
        </button>

        {rolesExpanded && (
          <div className="px-6 pb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(allRoles ?? []).map((role) => {
              const info = ROLE_DESCRIPTIONS[role.name];
              return (
                <Box
                  key={role.id}
                  className="rounded-md border border-borderColor p-4 flex flex-col gap-1"
                >
                  <HStack className="items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-headerColor">
                      {role.name}
                    </p>
                    {info && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                        {info.scope}
                      </span>
                    )}
                  </HStack>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {info?.description ??
                      "Custom role with a specific set of permissions."}
                  </p>
                </Box>
              );
            })}
          </div>
        )}
      </Box>

      {/* Footer actions */}
      <HStack className="items-center justify-end gap-3 px-6 py-4 bg-white rounded-lg border border-borderColor drop-shadow-box">
        {hasUnsavedChanges && (
          <ButtonPrimary1 onClick={discardChanges}>
            Discard Changes
          </ButtonPrimary1>
        )}
        <ButtonPrimary
          onClick={handleSave}
          loading={isSaving}
          disabled={!hasUnsavedChanges}
        >
          Save Changes
        </ButtonPrimary>
      </HStack>

      {/* Add User dialog */}
      <Dialog open={addDialog} maxWidth="sm" fullWidth>
        <AddUserDialog
          allRoles={allRoles ?? []}
          existingEmails={existingEmails}
          onClose={() => setAddDialog(false)}
          onAdd={handleAddUser}
        />
      </Dialog>

      {/* Edit Permissions dialog */}
      {permEditTarget &&
        (() => {
          const targetRow = rows.find((r) => r._localId === permEditTarget);
          const savedPerms: AclList[] = targetRow?.customPermissions ?? [];
          const currentPerms: AclList[] = [
            ...DEFAULT_MANAGER_PERMISSIONS,
            ...savedPerms.filter(
              (p) =>
                !DEFAULT_MANAGER_PERMISSIONS.some(
                  (d) => d.resource === p.resource && d.action === p.action
                )
            ),
          ];
          return (
            <Dialog
              open
              maxWidth="sm"
              fullWidth
              onClose={() => setPermEditTarget(null)}
            >
              <EditPermissionsDialog
                initialPerms={currentPerms}
                userName={targetRow?.name || targetRow?.email || ""}
                onClose={() => setPermEditTarget(null)}
                onSave={handleSavePermissions}
              />
            </Dialog>
          );
        })()}

      {/* Confirm delete dialog */}
      <Dialog open={confirmDelete} maxWidth="sm">
        <AlertDialog
          open={confirmDelete}
          message={`Are you sure you want to delete ${selected.size} ${
            selected.size === 1 ? "user" : "users"
          }? This will be applied when you save.`}
          primaryActionText="Delete"
          secondaryActionText="Cancel"
          onClose={() => setConfirmDelete(false)}
          onPrimaryAction={handleDeleteSelected}
          onSecondaryAction={() => setConfirmDelete(false)}
        />
      </Dialog>
    </VStack>
  );
}

/* ── Row status styles ─────────────────────────────────────────────── */

const STATUS_ROW_BG: Record<RowStatus, string> = {
  pristine: "",
  new: "bg-green-50/60",
  modified: "bg-blue-50/40",
  deleted: "bg-red-50/60",
};

const STATUS_LEFT_BORDER: Record<RowStatus, string> = {
  pristine: "",
  new: "border-l-4 border-l-green-400",
  modified: "border-l-4 border-l-blue-400",
  deleted: "border-l-4 border-l-red-400",
};

const STATUS_LABEL: Record<RowStatus, { text: string; cls: string } | null> = {
  pristine: null,
  new: { text: "will be added", cls: "text-green-400" },
  modified: { text: "will be edited", cls: "text-blue-400" },
  deleted: { text: "will be deleted", cls: "text-red-400" },
};

/* ── Row component ─────────────────────────────────────────────────── */

function UserTableRow({
  row,
  isCurrentUser,
  isSelected,
  allRoles,
  onToggle,
  onRoleChange,
  onDelete,
  onUndo,
  onEditPermissions,
}: {
  row: UserRow;
  isCurrentUser: boolean;
  isSelected: boolean;
  allRoles: Roles[];
  onToggle: () => void;
  onRoleChange: (name: string) => void;
  onDelete: () => void;
  onUndo: () => void;
  onEditPermissions?: () => void;
}): React.ReactElement {
  const statusLabel = STATUS_LABEL[row._status];
  const isDeleted = row._status === "deleted";
  const inputDisabled = isCurrentUser || isDeleted;

  return (
    <tr className={`${STATUS_ROW_BG[row._status]} transition-colors`}>
      {/* Checkbox - carries the left-border status colour */}
      <td className={`px-6 py-3 ${STATUS_LEFT_BORDER[row._status]}`}>
        {!isCurrentUser && !isDeleted && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggle}
            className="rounded cursor-pointer accent-primary"
          />
        )}
      </td>

      {/* Name */}
      <td className="px-4 py-3 text-sm text-primaryText">
        <HStack className="items-center gap-2 flex-wrap">
          <span
            className={
              isDeleted
                ? "line-through text-gray-400"
                : row.name
                ? ""
                : "italic text-gray-400"
            }
          >
            {row.name || "Pending"}
          </span>
          {statusLabel && (
            <span className={`pl-0.5 text-xxs ${statusLabel.cls}`}>
              {statusLabel.text}
            </span>
          )}
          {isCurrentUser && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
              You
            </span>
          )}
        </HStack>
      </td>

      {/* Email */}
      <td
        className={`px-4 py-3 text-sm text-gray-600 ${
          isDeleted ? "line-through text-gray-400" : ""
        }`}
      >
        {row.email}
      </td>

      {/* Role */}
      <td className="px-4 py-3">
        <Select
          options={allRoles}
          valueGetter={(r) => r.name}
          textGetter={(r) => r.name}
          value={row.role?.name ?? ""}
          onChange={(e) => onRoleChange(e.target.value)}
          disabled={inputDisabled}
          className="text-sm py-1 pr-6 disabled:bg-transparent disabled:border-transparent disabled:cursor-default"
        />
      </td>

      {/* Member Since */}
      <td
        className={`px-4 py-3 text-sm text-gray-500 ${
          isDeleted ? "line-through text-gray-400" : ""
        }`}
      >
        {row._status === "new"
          ? ""
          : new Date(row.userSince).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-right">
        {!isCurrentUser && (
          <CTADropdown
            actions={
              isDeleted
                ? [{ name: "Undo Delete" }]
                : [
                    ...(row.role?.name === "MANAGER"
                      ? [{ name: "Edit Permissions" }]
                      : []),
                    { name: "Delete User" },
                  ]
            }
            onAction={(action) => {
              if (action.name === "Delete User") onDelete();
              if (action.name === "Undo Delete") onUndo();
              if (action.name === "Edit Permissions") onEditPermissions?.();
            }}
          />
        )}
      </td>
    </tr>
  );
}

/* ── Add User dialog ───────────────────────────────────────────────── */

const addUserSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
  roleId: Yup.string().required("Role is required"),
});

const DEFAULT_MANAGER_PERMISSIONS: AclList[] = [
  { resource: Resource.Employee, action: Action.Read },
  { resource: Resource.Grant, action: Action.Read },
  { resource: Resource.Company, action: Action.Read },
];

function AddUserDialog({
  allRoles,
  existingEmails,
  onClose,
  onAdd,
}: {
  allRoles: Roles[];
  existingEmails: string[];
  onClose: () => void;
  onAdd: (values: {
    name: string;
    email: string;
    roleId: string;
    customPermissions?: AclList[];
  }) => void;
}) {
  const [customPermissions, setCustomPermissions] = React.useState<AclList[]>(
    DEFAULT_MANAGER_PERMISSIONS
  );

  function handleSubmit(values: {
    name: string;
    email: string;
    roleId: string;
  }) {
    const selectedRole = allRoles.find((r) => r.id === values.roleId);
    const isManager = selectedRole?.name === "MANAGER";
    onAdd({
      ...values,
      customPermissions: isManager ? customPermissions : undefined,
    });
  }

  return (
    <VStack className="w-full">
      {/* Header */}
      <HStack className="items-center justify-between px-6 py-5 border-b border-borderColor">
        <p className="text-lg font-semibold text-headerColor">Add User</p>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Icon icon="lucide:x" width={20} />
        </button>
      </HStack>

      {/* Form */}
      <Formik
        initialValues={{ name: "", email: "", roleId: allRoles[0]?.id ?? "" }}
        validationSchema={addUserSchema}
        validate={(values) => {
          const errors: Record<string, string> = {};
          if (existingEmails.includes(values.email.trim().toLowerCase())) {
            errors.email = "This user has already been added";
          }
          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const selectedRole = allRoles.find(
            (r) => r.id === formik.values.roleId
          );
          const isManager = selectedRole?.name === "MANAGER";
          return (
            <Form>
              <VStack className="gap-6 px-6 py-7">
                {/* Name + Email */}
                <HStack className="gap-4">
                  <div className="flex-1">
                    <Label className="required text-sm font-medium mb-1">
                      Full name
                    </Label>
                    <Input
                      type="text"
                      placeholder="Jane Smith"
                      className="w-full"
                      {...formik.getFieldProps("name")}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <FormError text={formik.errors.name} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="required text-sm font-medium mb-1">
                      Email address
                    </Label>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      className="w-full"
                      {...formik.getFieldProps("email")}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <FormError text={formik.errors.email} />
                    )}
                  </div>
                </HStack>

                {/* Role */}
                <div>
                  <Label className="required text-sm font-medium mb-1">
                    Role
                  </Label>
                  <Select
                    options={allRoles}
                    valueGetter={(r) => r.id}
                    textGetter={(r) => r.name}
                    className="w-full text-sm"
                    {...formik.getFieldProps("roleId")}
                    onChange={(e) => {
                      formik.setFieldValue("roleId", e.target.value);
                      setCustomPermissions(DEFAULT_MANAGER_PERMISSIONS);
                    }}
                  />
                  {formik.touched.roleId && formik.errors.roleId && (
                    <FormError text={formik.errors.roleId} />
                  )}
                </div>

                {/* Permission picker — only for MANAGER role */}
                {isManager && (
                  <div className="border border-borderColor rounded-lg p-4 bg-gray-50">
                    <ManagerPermissionPicker
                      value={customPermissions}
                      onChange={setCustomPermissions}
                    />
                  </div>
                )}
              </VStack>

              {/* Footer */}
              <HStack className="items-center justify-end gap-3 px-6 pb-6">
                <ButtonPrimary1 type="button" onClick={onClose}>
                  Cancel
                </ButtonPrimary1>
                <ButtonPrimary
                  type="submit"
                  disabled={!formik.isValid || !formik.dirty}
                >
                  Add User
                </ButtonPrimary>
              </HStack>
            </Form>
          );
        }}
      </Formik>
    </VStack>
  );
}

export default UserRoles;
